import {
  debounceTime,
  distinctUntilChanged,
  pluck,
  tap,
  startWith,
  map,
} from 'rxjs/operators';
import isEqual from 'lodash/isEqual';
import store, { SET_LOADING } from '@/store';
import http from '../helpers/new-http';

const dataTableMixin = {
  data: () => ({
    loading: false,
    total: 0,
    options: {},
  }),
  computed: {
    httpParams() {
      return { ...this.options };
    },
    onOptionsUpdate$() {
      return this.$watchAsObservable('options', { deep: true });
    },
    onKeywordUpdate$() {
      return this.$watchAsObservable('keyword')
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => {
            this.options.page = 1;
          }),
        );
    },
    onFilterUpdate$() {
      return this.$watchAsObservable('filter', { deep: true }).pipe(
        map(({ newValue }) => ({ ...newValue })),
        distinctUntilChanged((x, y) => isEqual(x, y)),
        tap(() => {
          this.options.page = 1;
        }),
      );
    },
    onPathUpdate$() {
      return this.$watchAsObservable('$route.path').pipe(
        startWith(''),
      );
    },
    onDateRangeUpdate$() {
      return this.$watchAsObservable('dateRange').pipe(
        pluck('newValue'),
        tap((dateRange) => {
          if (dateRange) {
            [this.filter.startDate, this.filter.endDate] = dateRange;
          } else {
            this.filter.startDate = null;
            this.filter.endDate = null;
          }
        }),
      );
    },
    scheduleTasks() {
      return [
        this.onOptionsUpdate$,
        this.onKeywordUpdate$,
        this.onFilterUpdate$,
        this.onPathUpdate$,
        this.onDateRangeUpdate$,
      ];
    },
    selectedKeywordAndFilters() {
      let status = '';
      if (this.filter.status) {
        status = this.statusSelectOptions.filter(
          (option) => this.filter.status === option.value,
        )[0].text;
      }
      let deliveryStatus = '';
      if (this.filter.deliveryStatus) {
        deliveryStatus = this.deliveryStatusSelectOptions.filter(
          (option) => this.filter.deliveryStatus === option.value,
        )[0].text;
      }
      const categoryTags = this.filter.categoryTagId
        ? this.categoryTagOptions$?.filter((tag) => tag.id === this.filter.categoryTagId)[0].name
        : '';
      const promoTags = this.filter.promoTagId
        ? this.promoTagOptions$?.filter((tag) => tag.id === this.filter.promoTagId)[0].name
        : '';
      let keyword = '';
      if (this.keyword) {
        for (let i = 0; i < this.filteredKeywordSelectOptions.length; i += 1) {
          if (this.filteredKeywordSelectOptions[i].value === this.filter.keywordType) {
            keyword = this.filteredKeywordSelectOptions[i].text;
            break;
          }
        }
      }
      const dateRange = this.dateRange ? this.dateRange.join(' - ') : '';
      const userClass = this.filter.class
        ? this.userClassSelectOptions.filter((option) => option.value === this.filter.class)[0].text : '';
      const isShown = this.filter.isShown
        ? this.isShownSelectOptions.filter((option) => option.value === this.filter.isShown)[0].text : '';
      const type = this.filter.type
        ? this.typeSelectOptions.filter((option) => option.value === this.filter.type)[0].text : '';
      const displayLocation = this.filter.displayLocation
        ? this.displayLocationSelectOptions.filter((option) => option.value === this.filter.displayLocation)[0].text : '';

      if (keyword || this.keyword || categoryTags || status || dateRange || userClass
        || promoTags || isShown || type || displayLocation || deliveryStatus) {
        return `${keyword} ${this.keyword} ${categoryTags} ${status} ${dateRange} ${userClass} 
        ${promoTags} ${isShown} ${type} ${displayLocation} ${deliveryStatus}`;
      }
      return null;
    },
    filteredKeywordSelectOptions() {
      const roleOptions = this.keywordSelectOptions.filter(
        (o) => !o.roles || o.roles.includes(this.role),
      );
      if (this.role === 'admin') {
        return roleOptions;
      }
      return roleOptions.filter((option) => !option.adminOnly);
    },
    filteredStatusSelectOptions() {
      const roleOptions = this.statusSelectOptions.filter(
        (o) => !o.roles || o.roles.includes(this.role),
      );
      if (this.role === 'merchant') {
        return roleOptions;
      }
      return roleOptions.filter((option) => !option.merchantOnly);
    },
    filteredDeliveryStatusSelectOptions() {
      const roleOptions = this.deliveryStatusSelectOptions.filter(
        (o) => !o.roles || o.roles.includes(this.role),
      );
      if (this.role === 'merchant') {
        return roleOptions;
      }
      return roleOptions.filter((option) => !option.merchantOnly);
    },
    filteredIsShownSelectOptions() {
      const roleOptions = this.isShownSelectOptions.filter(
        (o) => !o.roles || o.roles.includes(this.role),
      );
      if (this.role === 'admin') {
        return roleOptions;
      }
      return roleOptions.filter((option) => !option.adminOnly);
    },
    filteredTypeSelectOptions() {
      const roleOptions = this.typeSelectOptions.filter(
        (o) => !o.roles || o.roles.includes(this.role),
      );
      if (this.role === 'admin') {
        return roleOptions;
      }
      return roleOptions.filter((option) => !option.adminOnly);
    },
    filteredDisplayLocationSelectOptions() {
      const roleOptions = this.displayLocationSelectOptions.filter(
        (o) => !o.roles || o.roles.includes(this.role),
      );
      if (this.role === 'admin') {
        return roleOptions;
      }
      return roleOptions.filter((option) => !option.adminOnly);
    },
  },
  // mounted() {
  //   this.options = {
  //     sortBy: this.defaultSortBy,
  //     order: this.defaultOrder,
  //   };
  // },
  methods: {
    httpRequest$(options, data) {
      return http({
        ...options,
        params: {
          ...this.httpParams,
          ...data,
        },
      }, {
        next: (response) => {
          if (response?.result?.list) {
            const total = response.result?.list?.total;
            this.total = total > 0 ? total : 0;
          } else {
            const { total } = response;
            if (total || total === 0) {
              this.total = total > 0 ? total : 0;
            }
          }
        },
        complete: () => {
          this.loading = false;
          store.commit(SET_LOADING, false);
        },
      }).pipe(
        pluck('result'),
      );
    },
  },
};

export default dataTableMixin;
